import { useEffect, useMemo, useState } from "react";
import facade from "../services/qwof/apiFacade";
import Spinner from "./Spinner.jsx";
import QuagganEmoji from "./QuagganEmoji.jsx";

const normalizeName = (value) => (value || "").trim().toLowerCase();

function parseNames(value) {
  const seen = new Set();
  return value
    .split(/[\n,]/)
    .map((name) => name.trim())
    .filter((name) => {
      const normalized = normalizeName(name);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function NonparticipantsTab({ nonparticipants, loading, error, onRefresh, onChanged }) {
  const [input, setInput] = useState("");
  const [processing, setProcessing] = useState("");
  const [message, setMessage] = useState("");
  const [localError, setLocalError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const parsedNames = useMemo(() => parseNames(input), [input]);

  useEffect(() => {
    setLocalError("");
    setMessage("");
  }, [nonparticipants]);

  const handleCreate = async () => {
    if (parsedNames.length === 0) return;
    setProcessing("create");
    setLocalError("");
    setMessage("");
    try {
      const created = await facade.createNonparticipants(parsedNames.map((name) => ({ name })));
      const createdCount = Array.isArray(created) ? created.length : 0;
      setMessage(`Created ${createdCount}, skipped ${parsedNames.length - createdCount} (already existed)`);
      setInput("");
      await onChanged();
    } catch (err) {
      const msg = err?.fullError || err?.message || "Unknown error";
      setLocalError(`Failed to create nonparticipants: ${msg}`);
    } finally {
      setProcessing("");
    }
  };

  const handleUpdate = async (nonparticipant) => {
    const name = editingName.trim();
    if (!name) {
      setLocalError("Name cannot be empty.");
      return;
    }

    setProcessing(`update-${nonparticipant.id}`);
    setLocalError("");
    setMessage("");
    try {
      await facade.updateNonparticipant(nonparticipant.id, { name });
      setEditingId(null);
      await onChanged();
      setMessage("Nonparticipant updated.");
    } catch (err) {
      const msg = err?.status === 409
        ? "A nonparticipant with this name already exists"
        : (err?.fullError || err?.message || "Unknown error");
      setLocalError(`Failed to update nonparticipant: ${msg}`);
    } finally {
      setProcessing("");
    }
  };

  const handleDelete = async (nonparticipant) => {
    if (!window.confirm(`Delete "${nonparticipant.name}" from nonparticipants?`)) return;

    setProcessing(`delete-${nonparticipant.id}`);
    setLocalError("");
    setMessage("");
    try {
      await facade.deleteNonparticipant(nonparticipant.id);
      await onChanged();
      setMessage("Nonparticipant deleted.");
    } catch (err) {
      const msg = err?.fullError || err?.message || "Unknown error";
      setLocalError(`Failed to delete nonparticipant: ${msg}`);
    } finally {
      setProcessing("");
    }
  };

  const displayedError = localError || error;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, color: "#40e8ff" }}>Nonparticipants</h3>
          <p className="login-help-text" style={{ margin: "6px 0 0" }}>
            Manage wheel slots that should not count as real participants.
          </p>
        </div>
        <button className="names-btn secondary" onClick={onRefresh} disabled={loading || !!processing}>
          Refresh
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        <textarea
          className="login-input"
          rows={5}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="One name per line or comma-separated"
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
        />
        <p className="login-help-text" style={{ margin: "6px 0 8px" }}>
          {parsedNames.length} unique name{parsedNames.length === 1 ? "" : "s"} ready to create.
        </p>
        <button className="names-btn primary" onClick={handleCreate} disabled={parsedNames.length === 0 || !!processing}>
          {processing === "create" ? "Creating..." : "Create nonparticipants"}
        </button>
      </div>

      {displayedError ? (
        <>
          <pre className="register-error" style={{ whiteSpace: "pre-wrap", marginTop: 12 }}>{displayedError}</pre>
          <QuagganEmoji type="failure" />
        </>
      ) : null}
      {message ? <p className="login-help-text" style={{ color: "#9ef0b0" }}>{message}</p> : null}

      {loading ? <Spinner /> : nonparticipants.length === 0 ? (
        <div className="login-help-text" style={{ marginTop: 18 }}>No nonparticipants found.</div>
      ) : (
        <div style={{ marginTop: 18, display: "grid", gap: 8 }}>
          {nonparticipants.map((nonparticipant) => {
            const editing = editingId === nonparticipant.id;
            const busy = processing === `update-${nonparticipant.id}` || processing === `delete-${nonparticipant.id}`;
            return (
              <div
                key={nonparticipant.id}
                style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: 10, borderRadius: 10, background: "rgba(8, 24, 50, 0.6)" }}
              >
                {editing ? (
                  <>
                    <input
                      className="login-input"
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      disabled={busy}
                      style={{ flex: "1 1 240px" }}
                    />
                    <button className="names-btn primary" onClick={() => handleUpdate(nonparticipant)} disabled={busy}>Save</button>
                    <button className="names-btn secondary" onClick={() => setEditingId(null)} disabled={busy}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: "1 1 240px", color: "#eefbff", fontWeight: 700 }}>{nonparticipant.name}</span>
                    <button
                      className="names-btn secondary"
                      onClick={() => {
                        setEditingId(nonparticipant.id);
                        setEditingName(nonparticipant.name || "");
                        setLocalError("");
                      }}
                      disabled={!!processing}
                    >
                      Edit
                    </button>
                    <button className="names-btn secondary" onClick={() => handleDelete(nonparticipant)} disabled={!!processing}>
                      {busy ? "Deleting..." : "Delete"}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NonparticipantsTab;
